import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import DreamerFieldCard from "@/components/controls/DreamerFieldCard";
import DreamerEntityMatchRow from "@/components/controls/DreamerEntityMatchRow";
import { useCheckPersonDuplicates } from "@/entities/people/hooks";
import { useCheckCompanyDuplicates } from "@/pages/companies/hooks";
import { extractDomain, isPersonalEmailDomain } from "@/utils/domains";
import { emailRegex } from "@/utils/validation";

// Shorter than the manual tab's 500ms — the rows are only useful mid-typing. Exported so tests can't drift.
export const LOOKUP_DEBOUNCE_MS = 250;

interface Props {
  email: string;
  setEmail: (email: string) => void;
  /** Set by the parent once a submit has been attempted, so the field isn't red while typing. */
  showValidation: boolean;
}

/** "With Dreamer" tab: one email field Dreamer enriches into a full person; matches shown are advisory only. */
const CreatePersonWithDreamer: React.FC<Props> = ({
  email,
  setEmail,
  showValidation
}) => {
  const { t } = useTranslation();
  const [debouncedEmail, setDebouncedEmail] = useState("");

  const trimmedEmail = email.trim();
  const isInvalid =
    showValidation && trimmedEmail.length > 0 && !emailRegex.test(trimmedEmail);
  const isPersonalEmail =
    emailRegex.test(trimmedEmail) && isPersonalEmailDomain(trimmedEmail);

  useEffect(() => {
    if (!trimmedEmail) {
      setDebouncedEmail("");
      return;
    }
    const timer = setTimeout(
      () => setDebouncedEmail(trimmedEmail),
      LOOKUP_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [trimmedEmail]);

  // The person check matches the exact email, so it runs for any complete address;
  // only the domain-based company lookup is meaningless for personal-mail providers.
  // Also requires the debounce to have settled — otherwise rows for the PRIOR email
  // linger for up to 250ms after the user edits or clears it.
  const isCompleteEmail =
    debouncedEmail === trimmedEmail && emailRegex.test(debouncedEmail);
  const isCompanyLookupEmail =
    isCompleteEmail && !isPersonalEmailDomain(debouncedEmail);
  const emailDomain = isCompanyLookupEmail
    ? (extractDomain(debouncedEmail) ?? "")
    : "";

  const personCheck = useCheckPersonDuplicates(debouncedEmail, isCompleteEmail);
  const companyCheck = useCheckCompanyDuplicates(
    emailDomain,
    emailDomain.length > 0
  );

  const existingPerson = personCheck.data?.person;
  const existingPersonName = existingPerson
    ? [existingPerson.firstName, existingPerson.lastName]
        .filter(Boolean)
        .join(" ") || existingPerson.email
    : null;

  // Exact domain match decides existing-vs-created; similar matches aren't "the" company for this email.
  const matchedCompany = companyCheck.data?.exactMatches?.[0];
  // Each row is gated on its own lookup's data (not isFetching, and not each other's):
  // "Will be created" must not flash mid-flight, and a slow company lookup must not hide a resolved person match.
  const showPersonRow =
    isCompleteEmail && Boolean(existingPerson && existingPersonName);
  const showCompanyRow =
    isCompanyLookupEmail && companyCheck.data !== undefined;

  return (
    <DreamerFieldCard
      hint={t("people.createModal.withDreamer.hint")}
      label={t("people.createModal.withDreamer.emailLabel")}
      placeholder={t("people.createModal.withDreamer.emailPlaceholder")}
      value={email}
      onChange={setEmail}
      error={isInvalid ? t("people.createModal.withDreamer.invalidEmail") : null}
      notice={
        isPersonalEmail
          ? t("people.createModal.withDreamer.personalDomain")
          : null
      }
      dataTest="create-person-dreamer-email"
    >
      {showPersonRow || showCompanyRow ? (
        <div className="mt-4 flex flex-col gap-3">
          {showPersonRow ? (
            <DreamerEntityMatchRow
              entityType={EntityType.PERSON}
              title={existingPersonName ?? ""}
              subtitle={t("dreamerCreate.personEntity")}
              status="existing"
              badgeLabel={t("dreamerCreate.alreadyInCrm")}
              dataTest="create-person-dreamer-person-match"
            />
          ) : null}
          {showCompanyRow ? (
            <DreamerEntityMatchRow
              entityType={EntityType.COMPANY}
              title={
                matchedCompany?.companyName ??
                t("people.createModal.withDreamer.noCompanyMatch")
              }
              subtitle={
                matchedCompany ? t("dreamerCreate.companyEntity") : undefined
              }
              status={matchedCompany ? "existing" : "new"}
              badgeLabel={
                matchedCompany
                  ? t("dreamerCreate.alreadyInCrm")
                  : t("dreamerCreate.willBeCreated")
              }
              dataTest="create-person-dreamer-company-match"
            />
          ) : null}
        </div>
      ) : null}
    </DreamerFieldCard>
  );
};

export default CreatePersonWithDreamer;
